import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { ShippingProtection } from '@theme/shipping-protection';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/** Pairs cart line item rows across a morph by their stable `data-key`, so an insertion doesn't reuse a neighbouring row's DOM node (and its still-loading image) for a different product. */
const cartLineNodeKey = (/** @type {Node} */ node) => (node instanceof HTMLElement ? node.dataset.key : undefined);

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // The selector names its row by position among the rendered rows, which is the one thing a
    // position is still good for. The cart is addressed by key from here on.
    const lineItemRow = line ? this.refs.cartItemRows[line - 1] : undefined;
    const key = lineItemRow?.dataset.key;

    if (!key) return;

    if (quantity === 0) {
      return this.onLineItemRemove(key);
    }

    this.updateQuantity({
      key,
      quantity,
      action: 'change',
    });

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow?.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {string} key - The line item key.
   */
  onLineItemRemove(key) {
    const cartItemRowToRemove = this.refs.cartItemRows.find((row) => row.dataset.key === key);

    const { doughGroup } = cartItemRowToRemove?.dataset ?? {};

    const rowsToRemove = [];

    if (doughGroup) {
      // One set is one subscription, so its lines go together.
      rowsToRemove.push(...this.refs.cartItemRows.filter((row) => row.dataset.doughGroup === doughGroup));
    } else if (cartItemRowToRemove) {
      rowsToRemove.push(
        cartItemRowToRemove,
        // Get all nested lines of the row to remove
        ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === key),
      );
    }

    // The protection row holds its cart line's position without a shopper seeing it, so it is not
    // counted among what they have left.
    const shopperRows = this.refs.cartItemRows.filter(
      (row) => !row.classList.contains('cart-items__table-row--protection'),
    );

    const orphaned = shopperRows.filter((row) => !rowsToRemove.includes(row));
    const dropsGift =
      !doughGroup &&
      orphaned.length > 0 &&
      orphaned.every((row) => row.classList.contains('cart-items__table-row--free-gift'));

    if (dropsGift) rowsToRemove.push(...orphaned);

    const clearsCart = rowsToRemove.length === shopperRows.length;

    // A cart with nothing left to protect keeps no charge. It goes in this request rather than a
    // later one of its own: a separate write renumbers the cart's lines, and `/cart/change.js`
    // addresses them by position.
    const protectionKey = ShippingProtection.lineKey;
    const dropsProtection = Boolean(protectionKey) && clearsCart;

    const keys = rowsToRemove.map((row) => row.dataset.key).filter(Boolean);
    if (dropsProtection && protectionKey) keys.push(protectionKey);

    this.updateQuantity({
      key,
      quantity: 0,
      action: 'clear',
      // Only a set, the gift case and the protection line need `updates`; a bundle's child lines
      // still cascade from its parent.
      keys: doughGroup || dropsGift || dropsProtection ? keys : undefined,
    });

    if (!cartItemRowToRemove) return;

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = clearsCart;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {string} config.key - The line item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string[]} [config.keys] - Keys to zero together, where one line is not enough.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { key, quantity, keys } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      // `updates` zeroes several lines at once, which one `id`/`quantity` pair cannot express.
      ...(keys?.length ? { updates: Object.fromEntries(keys.map((lineKey) => [lineKey, 0])) } : { id: key, quantity }),
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(keys?.length ? Theme.routes.cart_update_url : Theme.routes.cart_change_url, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(key, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html',
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            // Adds and updates share an event name, and a shopper editing the cart is
            // already looking at it.
            autoOpen: false,
            sections: parsedResponseText.sections,
          }),
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
          getNodeKey: cartLineNodeKey,
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {string} key - The line item key.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (key, parsedResponseText) => {
    const row = this.refs.cartItemRows.find((cartItemRow) => cartItemRow.dataset.key === key);
    const quantityInput = row?.querySelector('input[data-cart-line]');

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${key}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${key}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false, getNodeKey: cartLineNodeKey });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, { getNodeKey: cartLineNodeKey });

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false, getNodeKey: cartLineNodeKey });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
